import { FunctionalComponent } from 'preact';
import { useLayoutEffect, useRef, useState } from 'preact/hooks';

import { useCheckoutContext } from '@primer-io/shared-library/contexts';
import { PaymentMethodType } from '../enums/Tokens';
import { BasePaymentMethod } from '../payment-methods/BasePaymentMethod';
import { ImplementationType } from '../payment-methods/payment-method-implementations/types';
import { useModule, useDisableComponent } from '../utils/hooks';
import { getPaymentMethodUrl } from '../utils/ModuleResolver';
import { SceneEnum } from '../enums/Checkout';
import { useWaitForComponent } from '../utils/SceneLoader/WaitForComponents';
import toCamelCase from '../utils/toCamelCase';
import BasePaymentMethodButton, {
  PaymentMethodButtonOptions,
} from '../payment-methods/Button';

type Props = {
  id: string;
  paymentMethod: BasePaymentMethod;
  onMounted?: () => void;
};

const ManagedPaymentMethodButton: FunctionalComponent<Props> = ({
  id,
  paymentMethod,
  onMounted,
}) => {
  const [mountError, setMountError] = useState(false);
  const rootRef = useRef<HTMLDivElement>();

  const { disableControls } = useDisableComponent({
    scene: SceneEnum.CHOOSE_PAYMENT_METHOD,
  });
  const disablePaymentMethod = (disabled) => {
    paymentMethod.setDisabled(disabled);
  };

  useLayoutEffect(() => {
    (async () => {
      try {
        const result = await paymentMethod.mount();
        if (result === false) {
          setMountError(true);
        }
      } catch (e) {
        setMountError(true);
      }
      onMounted?.();
      disablePaymentMethod(disableControls);
    })();
  }, []);

  useLayoutEffect(() => {
    disablePaymentMethod(disableControls);
  }, [disableControls, paymentMethod]);

  if (mountError) {
    return null;
  }

  if (
    paymentMethod.specs.implementationType === ImplementationType.WEB_REDIRECT
  ) {
    const { type, tokenize, remoteConfig } = paymentMethod;
    const { button } = remoteConfig.displayMetadata;
    const onClick = () => {
      tokenize();
    };

    return (
      <BasePaymentMethodButton
        iconWidth='100px'
        paymentMethod={paymentMethod}
        paymentMethodType={type}
        backgroundColor={button.backgroundColor.colored}
        iconSrc={button.iconUrl}
        iconAlt={type}
        onClick={onClick}
        text={button.text}
        textColor={button.textColor?.light || button.textColor?.dark}
      />
    );
  }

  return (
    <div
      ref={rootRef}
      id={`primer-checkout-apm-${toCamelCase(id)}`}
      className='PrimerCheckout__apmButton'
      tabIndex={-1}
    ></div>
  );
};

const ModuleMethodButton: FunctionalComponent<Props> = ({
  id,
  paymentMethod,
  onMounted,
}) => {
  const { context } = useCheckoutContext();
  const url = context
    ? getPaymentMethodUrl(context, id as PaymentMethodType)
    : undefined;

  const { Module, isLoading } = useModule<
    FunctionalComponent<{
      paymentMethod: BasePaymentMethod;
      onMounted?: () => void;
    }>
  >({
    remotePath: url ?? '',
    path: 'PaymentMethodButton',
    scope: toCamelCase(paymentMethod.type),
  });

  useLayoutEffect(() => {
    if (!Module && !isLoading) {
      console.log(`Cannot load button ${id}`);
      onMounted?.();
    }
  }, [Module, isLoading]);

  if (Module) {
    if (!paymentMethod.specs.hasExportedButtonOptions) {
      return <Module paymentMethod={paymentMethod} onMounted={onMounted} />;
    }
    const buttonOptions = (Module as unknown) as PaymentMethodButtonOptions;

    return (
      <BasePaymentMethodButton
        {...buttonOptions}
        buttonId={`primer-checkout-apm-${toCamelCase(paymentMethod.type)}`}
        paymentMethod={paymentMethod}
        onMounted={onMounted}
      />
    );
  }

  if (!isLoading && !Module) {
    return null;
  }

  return <div>Loading</div>;
};

const PaymentMethodButton: FunctionalComponent<Props> = ({
  id,
  paymentMethod,
}) => {
  const { wait, ready } = useWaitForComponent();

  useLayoutEffect(() => {
    wait(id);
  }, []);

  const handleMounted = () => {
    ready(id);
  };

  if (
    !paymentMethod.specs.buttonManagedByPaymentMethod &&
    !paymentMethod.specs.implementationType
  ) {
    return (
      <ModuleMethodButton
        id={id}
        paymentMethod={paymentMethod}
        onMounted={handleMounted}
      />
    );
  }

  return (
    <ManagedPaymentMethodButton
      id={id}
      paymentMethod={paymentMethod}
      onMounted={handleMounted}
    />
  );
};

export default PaymentMethodButton;
